import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";

const links = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/predictions", label: "Archive", testid: "nav-predictions" },
  { to: "/free-tips", label: "Free Tips", testid: "nav-free-tips" },
  { to: "/pricing", label: "Pricing", testid: "nav-pricing" },
  { to: "/about", label: "About", testid: "nav-about" },
  { to: "/faq", label: "FAQ", testid: "nav-faq" },
  { to: "/contact", label: "Contact", testid: "nav-contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-ink-950/85 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-2.5 group"
          >
            <div className="relative">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-700 to-emerald-900 flex items-center justify-center border border-gold-500/40 group-hover:border-gold-500 transition-colors">
                <TrendingUp className="w-5 h-5 text-gold-400" strokeWidth={2.5} />
              </div>
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-gold-500 animate-pulse-glow" />
            </div>
            <div className="leading-none">
              <div className="font-display font-bold text-lg sm:text-xl text-white tracking-tight">
                Asian<span className="gold-text">Picks</span>
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-500/80 mt-0.5">
                AH Predictions
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  cn(
                    "px-4 py-2 rounded-md text-sm font-medium transition-colors",
                    isActive
                      ? "text-gold-400 bg-white/5"
                      : "text-white/70 hover:text-white hover:bg-white/5"
                  )
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <Link
            to="/pricing"
            data-testid="nav-cta-plans"
            className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold-500 hover:bg-gold-400 text-ink-950 font-semibold text-sm transition-all shadow-lg shadow-gold-500/20"
          >
            View Plans
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md text-white hover:bg-white/10"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-white/10 bg-ink-950/95 backdrop-blur-xl"
        >
          <nav className="px-4 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-${l.testid}`}
                className={({ isActive }) =>
                  cn(
                    "px-4 py-3 rounded-md text-base font-medium transition-colors",
                    isActive
                      ? "text-gold-400 bg-white/5"
                      : "text-white/80 hover:bg-white/5"
                  )
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/pricing"
              data-testid="mobile-cta-plans"
              className="mt-2 inline-flex items-center justify-center px-5 py-3 rounded-full bg-gold-500 text-ink-950 font-semibold"
            >
              View Plans
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
